import React, { FC, useRef, useState } from "react";
import styled from "@emotion/styled";
import classNames from "classnames";

import Button from "../Button";

export const MAX_FILE_SIZE = 500 * 1024; // 500KB

export interface FileInputProps {
  label?: string;
  id: string;
  dark?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (file: File) => void;
}

const FileInput: FC<FileInputProps> = ({
  label,
  id,
  dark,
  error,
  required,
  disabled,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File>(null);
  const [isOversized, setIsOversized] = useState<boolean>(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    inputRef.current.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsOversized(false);
    if (e.target.files.length > 0) {
      const uploadedFile = e.target.files.item(0);
      if (uploadedFile.size > MAX_FILE_SIZE) return setIsOversized(true);
      setFile(uploadedFile);
      onChange?.(uploadedFile);
    }
  };

  return (
    <FileInputWrapper>
      {label && (
        <label
          htmlFor={id}
          className={["p2", dark].filter(Boolean).join(" ")}
        >
          {[label, required && "*"].filter(Boolean).join(" ")}
        </label>
      )}
      <input
        id={id}
        ref={inputRef}
        multiple={false}
        disabled={disabled}
        onChange={handleChange}
        className="w-hidden"
        name="attachment"
        accept=".pdf"
        type="file"
      />
      <FileInputRow>
        <Button
          buttonHref="#"
          buttonLabel="upload cv"
          buttonHrefClass="button w-button"
          linkProps={{ onClick: handleClick }}
          buttonLine={classNames("button-box", { [dark]: dark })}
          disabled={disabled}
          spacer={false}
        />
        <FileInputMessage
          className={classNames("p3", { [dark]: dark })}
          italic={!file}
        >
          <span
            style={{ marginBottom: (!file && isOversized) || error ? 10 : 0 }}
          >
            {file ? (
              <>
                {`${file.name} `}
                <small>({(file.size / 1024).toFixed(0)}KB/500KB)</small>
              </>
            ) : (
              <>
                {"No file chosen "}
                <small>(Format: PDF; Max. size: 500KB)</small>
              </>
            )}
          </span>
          {(!file && isOversized && (
            <span className="error-message">
              The file exceeds the file size limit.
            </span>
          )) ||
            (error && <span className="error-message">{error}</span>)}
        </FileInputMessage>
      </FileInputRow>
    </FileInputWrapper>
  );
};

const FileInputWrapper = styled.div`
  display: block;
`;

const FileInputRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FileInputMessage = styled.p<{ italic?: boolean }>`
  width: auto;
  line-height: 1;
  max-width: unset;
  margin-left: 16px;
  font-style: ${({ italic }) => (italic ? "italic" : "normal")};
  & > span {
    display: block;
  }
  & .error-message {
    margin-bottom: 0;
    color: #e31c79;
    font-style: normal;
    font-family: "Clan news", sans-serif;
  }
`;

export default FileInput;
